import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Edit3,
  ArrowUp,
  ArrowDown,
  Wand2,
  Upload,
  Link as LinkIcon,
  Image as ImageIcon,
  Loader2,
  Music,
  ExternalLink,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminAuth, AdminShell, adminHeaders } from "@/components/admin/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  id: null,
  title: "",
  artist: "",
  youtube: "",
  art: "",
  spotify: "",
  apple: "",
};

export default function AdminTracks() {
  return (
    <AdminAuth>
      <Inner />
    </AdminAuth>
  );
}

function Inner() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [artMode, setArtMode] = useState("url"); // 'url' | 'upload'
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tracks`);
      setTracks(res.data || []);
    } catch {
      toast.error("Failed to load tracks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setArtMode("url");
    setOpen(true);
  };

  const openEdit = (t) => {
    setForm({
      id: t.id,
      title: t.title,
      artist: t.artist,
      youtube: t.youtube,
      art: t.art,
      spotify: t.spotify || "",
      apple: t.apple || "",
    });
    setArtMode(t.art?.startsWith("data:") ? "upload" : "url");
    setOpen(true);
  };

  const onAutofill = async () => {
    if (!form.youtube.trim()) {
      toast.error("Paste a YouTube URL first.");
      return;
    }
    setFetchingMeta(true);
    try {
      const res = await axios.get(`${API}/admin/youtube-meta`, {
        params: { url: form.youtube.trim() },
        headers: adminHeaders(),
      });
      const meta = res.data;
      setForm((p) => ({
        ...p,
        title: p.title || meta.title || "",
        artist: p.artist || (meta.artist || "").replace(/ - Topic$/, ""),
        art: p.art || meta.thumbnail || "",
      }));
      toast.success("Pulled metadata from YouTube.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch metadata.");
    } finally {
      setFetchingMeta(false);
    }
  };

  const onUploadArt = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8 MB).");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, art: res.data.art }));
      toast.success("Cover art ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.title || !form.artist || !form.youtube || !form.art) {
      toast.error("Title, artist, YouTube URL and cover art are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        artist: form.artist,
        youtube: form.youtube,
        art: form.art,
        spotify: form.spotify || null,
        apple: form.apple || null,
      };
      if (form.id) {
        const res = await axios.patch(`${API}/tracks/${form.id}`, payload, {
          headers: adminHeaders(),
        });
        setTracks((arr) => arr.map((t) => (t.id === form.id ? res.data : t)));
        toast.success("Track updated.");
      } else {
        const res = await axios.post(`${API}/tracks`, payload, {
          headers: adminHeaders(),
        });
        setTracks((arr) => [...arr, res.data].sort((a, b) => a.sort_order - b.sort_order));
        toast.success("Track added to portfolio.");
      }
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm("Remove this track from the portfolio?")) return;
    try {
      await axios.delete(`${API}/tracks/${id}`, { headers: adminHeaders() });
      setTracks((arr) => arr.filter((t) => t.id !== id));
      toast.success("Track removed.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= tracks.length) return;
    const arr = [...tracks];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setTracks(arr);
    try {
      await axios.post(
        `${API}/tracks/reorder`,
        { ids: arr.map((t) => t.id) },
        { headers: adminHeaders() }
      );
    } catch {
      toast.error("Reorder failed.");
      load();
    }
  };

  return (
    <AdminShell onRefresh={load} refreshing={loading}>
      <div data-testid="admin-tracks-page">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50">
              Portfolio
            </p>
            <h1 className="mt-2 font-display font-black text-4xl tracking-tight text-white">
              Manage Catalog
            </h1>
            <p className="mt-2 font-body text-white/60 text-sm max-w-xl">
              Add, reorder or remove tracks featured on wereonesound.com. Up
              & down arrows reorder; new tracks land at the end.
            </p>
          </div>
          <Button
            type="button"
            onClick={openCreate}
            data-testid="admin-add-track"
            className="rounded-full h-12 px-6 bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
          >
            <Plus size={16} className="mr-2" /> Add Track
          </Button>
        </div>

        {loading && tracks.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-16 text-center text-white/40">
            <Loader2 className="mx-auto animate-spin" />
          </div>
        ) : tracks.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-16 text-center">
            <Music size={32} className="mx-auto text-white/30" />
            <p className="mt-4 font-display font-bold text-xl text-white">
              No tracks yet.
            </p>
            <p className="mt-2 font-body text-white/50 text-sm">
              Click "Add Track" to feature your first release.
            </p>
          </div>
        ) : (
          <ul
            className="space-y-3"
            data-testid="admin-tracks-list"
          >
            {tracks.map((t, idx) => (
              <li
                key={t.id}
                data-testid={`admin-track-${t.id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 lg:p-5 hover:bg-white/[0.04] transition-colors flex items-center gap-4"
              >
                <div className="flex flex-col gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    data-testid={`track-up-${t.id}`}
                    className="h-6 w-6 p-0 text-white/40 hover:text-white disabled:opacity-30"
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => move(idx, 1)}
                    disabled={idx === tracks.length - 1}
                    data-testid={`track-down-${t.id}`}
                    className="h-6 w-6 p-0 text-white/40 hover:text-white disabled:opacity-30"
                  >
                    <ArrowDown size={14} />
                  </Button>
                </div>

                <img
                  src={t.art}
                  alt=""
                  className="h-16 w-16 rounded-lg object-cover border border-white/10 shrink-0"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-extrabold text-base lg:text-lg text-white truncate">
                      {t.title}
                    </h3>
                    <span className="font-body text-[10px] uppercase tracking-[0.2em] text-white/40">
                      #{idx + 1}
                    </span>
                  </div>
                  <p className="font-body text-sm text-white/60 truncate">
                    {t.artist}
                  </p>
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-white/40">
                    <a
                      href={t.youtube}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:text-white"
                    >
                      YouTube <ExternalLink size={10} />
                    </a>
                    {t.spotify && (
                      <a
                        href={t.spotify}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-white"
                      >
                        Spotify <ExternalLink size={10} />
                      </a>
                    )}
                    {t.apple && (
                      <a
                        href={t.apple}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-white"
                      >
                        Apple <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => openEdit(t)}
                    data-testid={`track-edit-${t.id}`}
                    className="text-white/60 hover:text-white hover:bg-white/10 rounded-full h-9 w-9 p-0"
                  >
                    <Edit3 size={15} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onDelete(t.id)}
                    data-testid={`track-delete-${t.id}`}
                    className="text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-full h-9 w-9 p-0"
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="admin-track-dialog"
          className="bg-zinc-950 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {form.id ? "Edit Track" : "Add Track"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={onSave} className="space-y-5">
            <div className="space-y-2">
              <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                YouTube URL *
              </Label>
              <div className="flex gap-2">
                <Input
                  data-testid="track-input-youtube"
                  value={form.youtube}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, youtube: e.target.value }))
                  }
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
                <Button
                  type="button"
                  onClick={onAutofill}
                  disabled={fetchingMeta || !form.youtube}
                  data-testid="track-autofill"
                  className="h-12 rounded-xl bg-brand-magenta/90 hover:bg-brand-magenta text-white font-display font-bold whitespace-nowrap"
                >
                  {fetchingMeta ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <Wand2 size={14} className="mr-2" /> Auto-fill
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-white/40">
                Click Auto-fill to pull title, artist and cover art from
                YouTube.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Title *
                </Label>
                <Input
                  data-testid="track-input-title"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Artist *
                </Label>
                <Input
                  data-testid="track-input-artist"
                  value={form.artist}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, artist: e.target.value }))
                  }
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Cover Art *
                </Label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setArtMode("url")}
                    className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                      artMode === "url"
                        ? "bg-white text-black border-white"
                        : "border-white/10 text-white/60 hover:text-white"
                    }`}
                  >
                    <LinkIcon size={12} className="inline mr-1" /> URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setArtMode("upload")}
                    className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition-colors ${
                      artMode === "upload"
                        ? "bg-white text-black border-white"
                        : "border-white/10 text-white/60 hover:text-white"
                    }`}
                  >
                    <Upload size={12} className="inline mr-1" /> Upload
                  </button>
                </div>
              </div>

              {artMode === "url" ? (
                <Input
                  data-testid="track-input-art-url"
                  value={form.art.startsWith("data:") ? "" : form.art}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, art: e.target.value }))
                  }
                  placeholder="https://... or /assets/..."
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
              ) : (
                <div>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => onUploadArt(e.target.files?.[0])}
                    data-testid="track-input-art-file"
                  />
                  <Button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploading}
                    className="w-full h-12 rounded-xl bg-white/5 hover:bg-white/10 text-white border border-white/10 font-display font-bold"
                  >
                    {uploading ? (
                      <Loader2 size={14} className="animate-spin mr-2" />
                    ) : (
                      <Upload size={14} className="mr-2" />
                    )}
                    {uploading ? "Uploading..." : "Choose image (JPG/PNG/WebP, ≤8 MB)"}
                  </Button>
                </div>
              )}

              {form.art && (
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <img
                    src={form.art}
                    alt="cover preview"
                    className="h-16 w-16 rounded-lg object-cover border border-white/10"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">
                      Preview
                    </p>
                    <p className="text-xs text-white/60 truncate">
                      {form.art.startsWith("data:")
                        ? "Uploaded image"
                        : form.art}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setForm((p) => ({ ...p, art: "" }))}
                    className="text-white/40 hover:text-red-400 h-8 w-8 p-0"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Spotify URL (optional)
                </Label>
                <Input
                  data-testid="track-input-spotify"
                  value={form.spotify}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, spotify: e.target.value }))
                  }
                  placeholder="https://open.spotify.com/track/..."
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Apple Music URL (optional)
                </Label>
                <Input
                  data-testid="track-input-apple"
                  value={form.apple}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, apple: e.target.value }))
                  }
                  placeholder="https://music.apple.com/..."
                  className="bg-black/40 border-white/10 text-white h-12 rounded-xl focus-visible:ring-brand-magenta"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-full border-white/10 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="track-save"
                className="rounded-full bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
              >
                {saving ? "Saving..." : form.id ? "Save changes" : "Add to catalog"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
